import { useGetLabelByKey, useTranslation } from 'hooks/useLingUI';
import {
  selectAllLoadingFactory,
  selectDisciplinaFactory,
  selectDisciplineFactory,
  selectExpandedItemsFactory,
  selectFavouritesFactory,
  selectIsExpandedFactory,
  selectIsLoadingFactory,
  selectManifestazioneFactory,
  selectManifestazioniFactory,
  selectTs,
} from './selectors';
import { ExtendedDisciplinaMenuItem, LIVE_FAVOURITES, MenuParams } from './types';
import { buildMenu, isDisciplinaActive, miIppica } from './utility';

import { AppFragment } from 'enums/path-fragment';
import { shallowEqual } from 'fast-equals';
import { selectIsActiveMarcatori, selectIsActiveStreaming } from 'features/configuration/selectors';
import { selectCurrentPathFactory } from 'features/sport';
import produce from 'immer';
import { useTypedSelector } from 'lib/centralStore';
import { useMemo } from 'react';
import { SportsAvvenimentoEsposto } from 'types/swagger';
import { selectLiveAvvenimentoList } from '../liveInternalLayout/selectors';

export const useExpandedItems = () => {
  const selectExpandedItems = useMemo(selectExpandedItemsFactory, []);
  return useTypedSelector(selectExpandedItems, shallowEqual);
};

export const useIsExpanded = (id: string) => {
  const selectIsExpanded = useMemo(selectIsExpandedFactory, [id]);
  return useTypedSelector((state) => selectIsExpanded(state, id));
};

export const useIsLoading = (id?: string) => {
  const selectIsLoading = useMemo(selectIsLoadingFactory, [id]);
  return useTypedSelector((state) => selectIsLoading(state, id));
};

export const useMenu = ({ isTop, isLive }: MenuParams) => {
  const { t } = useTranslation();
  const ts = useTypedSelector(selectTs);
  const selectDiscipline = useMemo(selectDisciplineFactory, []);
  const raw = useTypedSelector(selectDiscipline);

  const isActiveMarcatori = useTypedSelector(selectIsActiveMarcatori);
  const isActiveStreaming = useTypedSelector(selectIsActiveStreaming);

  const discipline = buildMenu(t, raw, isTop, isLive, isActiveMarcatori, isActiveStreaming);
  // return useMemo(() => ({ts, discipline}),[ts, discipline]);
  return { ts, discipline };
};

export const useManifestazioni = (id: number) => {
  const selectManifestazioni = useMemo(selectManifestazioniFactory, [id]);
  const manifestazioni = useTypedSelector((state) => selectManifestazioni(state, id));
  return manifestazioni;
};

export const useDisciplina = (id: number | string, isLive?: Boolean | boolean) => {
  const selectDisciplina = useMemo(selectDisciplinaFactory, [id]);
  const selectCurrentPath = useMemo(selectCurrentPathFactory, [id]);

  const disciplina: ExtendedDisciplinaMenuItem = useTypedSelector((state) => selectDisciplina(state, id));

  const { slug: slugOri, isRoot, descrizione } = disciplina ?? {};

  const path = useTypedSelector(selectCurrentPath);
  const { isSelected, slug } = useMemo(() => {
    const wPath = (path ?? '').replace(/^\//gim, '').toLowerCase();
    const slug = `${isRoot ? slugOri : `${isLive ? AppFragment.Live : AppFragment.Sport}/${slugOri}`}`.toLowerCase();

    let isSelected = slug === wPath;

    if (!isSelected) {
      if (slug === miIppica.slug) {
        isSelected = wPath.startsWith(`${slug}/`);
      } else {
        isSelected = slug.includes('/') && wPath.startsWith(`${slug}/`);
      }
    }
    // console.log(`isSelected (${slug}, ${wPath}, ${path}) => ${isSelected}`)

    return { slug, isSelected };
  }, [path, slugOri, isLive, isRoot]);

  const isLoading = useIsLoading(slug);
  const isExpanded = useIsExpanded(`${disciplina?.id}`);
  const cmsDescription = useGetLabelByKey(`top-${isLive ? 'live' : 'sport'}-${slugOri}`);
  const manifestazioni = useManifestazioni(disciplina?.id ?? -Infinity);

  return {
    ...disciplina,
    slug,
    isRoot,
    isActive: isRoot || isDisciplinaActive(disciplina, isLive),
    isLoading,
    isSelected,
    isExpanded,
    descrizione: descrizione ?? cmsDescription ?? slugOri,
    manifestazioni,
  };
};

export const useManifestazione = (idDisciplina: number | string, idManifestazione: number | string) => {
  const { t } = useTranslation();
  const selectManifestazione = useMemo(selectManifestazioneFactory, [idDisciplina, idManifestazione]);
  const { scommesse: src, ...manifestazione } = useTypedSelector((state) =>
    selectManifestazione(state, idDisciplina, idManifestazione)
  );

  const isLoading = useIsLoading(manifestazione.slug);
  const isExpanded = useIsExpanded(`${idDisciplina}-${idManifestazione}`);

  const kScommesse = Object.keys(src?.scommessaMap ?? {});
  let scommesse = produce(src?.avvenimentoList ?? [], (draft: SportsAvvenimentoEsposto[]) => {
    draft.sort(
      (
        { dataOra: dtA, descrizione: desA, descrizioneTrKey: dKeyA },
        { dataOra: dtB, descrizione: desB, descrizioneTrKey: dKeyB }
      ) => {
        const aTime = new Date(dtA ?? '2020-10-10').getTime();
        const bTime = new Date(dtB ?? '2020-10-10').getTime();
        const result = aTime - bTime;
        if (result === 0) {
          return `${t(dKeyA, desA)}` > `${t(dKeyB, desB)}` ? 1 : -1;
        }
        return result;
      }
    );
  }).map((avv) => {
    const scommessaKey = kScommesse.find((key) => key.startsWith(`${avv.key}-`));
    if (!scommessaKey) return `${avv.key}--`;
    return scommessaKey;
  });

  return {
    ...manifestazione,
    isLoading,
    isExpanded,
    scommesse,
  };
};

export const useFavourites = () => {
  // KILLING FAVOURITES MANAGEMENT
  const isAuthenticated = false; // useIsAuthenticated();

  const selectAllLoading = useMemo(selectAllLoadingFactory, []);
  const selectLoadedItems = useMemo(selectFavouritesFactory, []);
  const selectLiveAvvenimenti = useMemo(selectLiveAvvenimentoList, []);

  const favourites = useTypedSelector((state) => selectLiveAvvenimenti(state));
  const counter = (favourites ?? []).length;

  const allLoading = useTypedSelector((state) => selectAllLoading(state));
  const loadedEvents = useTypedSelector((state) => selectLoadedItems(state));

  const isLoading = allLoading.includes(LIVE_FAVOURITES);
  const isExpanded = useIsExpanded(LIVE_FAVOURITES);

  return { counter, favourites, isLoading, allLoading, isExpanded, loadedEvents, isAuthenticated };
};

export const useIsFavourite = (avvenimentoKey: string) => {
  // KILLING FAVOURITES MANAGEMENT
  const isAuthenticated = false; // useIsAuthenticated();

  const selectLiveAvvenimenti = useMemo(selectLiveAvvenimentoList, [avvenimentoKey]);

  const favourites = useTypedSelector((state) => selectLiveAvvenimenti(state));

  let isFavourite = isAuthenticated && (favourites ?? []).some((x) => x.keyAvvenimento === avvenimentoKey);

  return {
    isFavourite,
    isAuthenticated,
  };
};
